import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ChevronDown, Check, X } from 'lucide-react';
import { useLlmProviders, type LlmProviderSafe, type TestResult } from '@/hooks/useLlmProviders';
import { LLM_PRESETS, type PresetKey } from '@/lib/llmPresets';
import { useToast } from '@/hooks/use-toast';

interface LlmProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: LlmProviderSafe;
  onSaved: () => void;
}

type Overrides = { arabic: string; english: string; fast: string };

const EMPTY_OVERRIDES: Overrides = { arabic: '', english: '', fast: '' };

export function LlmProviderDialog({ open, onOpenChange, provider, onSaved }: LlmProviderDialogProps) {
  const { toast } = useToast();
  const { testProvider, saveProvider } = useLlmProviders();

  const [label, setLabel] = useState('');
  const [preset, setPreset] = useState<PresetKey>('groq');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [modelOverrides, setModelOverrides] = useState<Overrides>(EMPTY_OVERRIDES);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowSaveWithoutTest, setAllowSaveWithoutTest] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Reset form whenever the dialog opens or the target provider changes.
  useEffect(() => {
    if (!open) return;
    setLabel(provider?.label ?? '');
    setPreset((provider?.preset as PresetKey) ?? 'groq');
    setBaseUrl(provider?.base_url ?? '');
    setApiKey('');
    setDefaultModel(provider?.default_model ?? '');
    setModelOverrides({
      arabic: provider?.model_overrides?.arabic ?? '',
      english: provider?.model_overrides?.english ?? '',
      fast: provider?.model_overrides?.fast ?? '',
    });
    setTestResult(null);
    setTesting(false);
    setSaving(false);
    setAllowSaveWithoutTest(false);
    setAdvancedOpen(false);
  }, [open, provider]);

  const handlePresetChange = (value: string) => {
    const key = value as PresetKey;
    setPreset(key);
    setBaseUrl((prev) => LLM_PRESETS[key].baseUrl || prev);
    setDefaultModel((prev) => LLM_PRESETS[key].suggestedModel || prev);
    setTestResult(null);
    setAllowSaveWithoutTest(false);
  };

  // Any edit to a connection-defining field invalidates a prior test.
  const invalidateTest = () => {
    setTestResult(null);
    setAllowSaveWithoutTest(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testProvider({
        id: provider?.id,
        baseUrl,
        apiKey: apiKey || undefined,
        defaultModel,
      });
      setTestResult(result);
      if (result.ok) {
        toast({ title: `Connection OK — ${result.httpStatus} in ${result.latencyMs} ms` });
      } else {
        toast({
          title: 'Connection test failed',
          description: `${result.httpStatus} ${result.error ?? ''}`.trim(),
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({ title: 'Connection test failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (skipTest: boolean) => {
    setSaving(true);
    try {
      await saveProvider({
        id: provider?.id,
        label,
        preset,
        baseUrl,
        apiKey: apiKey || undefined,
        defaultModel,
        modelOverrides,
        testToken: testResult?.testToken,
        skipTest: skipTest || undefined,
      });
      toast({ title: provider ? `Updated ${label}` : `Added ${label}` });
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Save failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWithoutTesting = () => {
    if (!window.confirm('Save this provider without a successful connection test? It may fail at request time.')) {
      return;
    }
    setAllowSaveWithoutTest(true);
    void handleSave(true);
  };

  const saveDisabled = saving || testing || !label.trim() || (!testResult?.ok && !allowSaveWithoutTest);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{provider ? 'Edit LLM Provider' : 'Add LLM Provider'}</DialogTitle>
          <DialogDescription>
            Configure an OpenAI-compatible endpoint. Test the connection before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="llm-label">Label</Label>
            <Input
              id="llm-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Groq production"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="llm-preset">Preset</Label>
            <Select value={preset} onValueChange={handlePresetChange}>
              <SelectTrigger id="llm-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(LLM_PRESETS) as PresetKey[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {LLM_PRESETS[key].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="llm-base-url">Base URL</Label>
            <Input
              id="llm-base-url"
              value={baseUrl}
              onChange={(e) => {
                setBaseUrl(e.target.value);
                invalidateTest();
              }}
              placeholder="https://api.example.com/v1"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="llm-api-key">
              API key{LLM_PRESETS[preset].keyless ? ' (optional)' : ''}
            </Label>
            <Input
              id="llm-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                invalidateTest();
              }}
              placeholder={provider?.api_key_last4 ? `•••• ${provider.api_key_last4}` : 'sk-...'}
            />
            {provider && (
              <p className="text-xs text-muted-foreground">Leave blank to keep the current key.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="llm-default-model">Default model</Label>
            <Input
              id="llm-default-model"
              value={defaultModel}
              onChange={(e) => {
                setDefaultModel(e.target.value);
                invalidateTest();
              }}
              placeholder="llama-3.3-70b-versatile"
            />
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="px-0 hover:bg-transparent">
                <ChevronDown
                  className={`h-4 w-4 me-1 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
                />
                Advanced — model overrides
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-3">
              <div className="space-y-1.5">
                <Label htmlFor="llm-override-arabic">Arabic model</Label>
                <Input
                  id="llm-override-arabic"
                  value={modelOverrides.arabic}
                  onChange={(e) => setModelOverrides((o) => ({ ...o, arabic: e.target.value }))}
                  placeholder="Inherit default model"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="llm-override-english">English model</Label>
                <Input
                  id="llm-override-english"
                  value={modelOverrides.english}
                  onChange={(e) => setModelOverrides((o) => ({ ...o, english: e.target.value }))}
                  placeholder="Inherit default model"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="llm-override-fast">Fast model</Label>
                <Input
                  id="llm-override-fast"
                  value={modelOverrides.fast}
                  onChange={(e) => setModelOverrides((o) => ({ ...o, fast: e.target.value }))}
                  placeholder="Inherit default model"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={handleTest} disabled={testing || saving}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Test connection
            </Button>
            {testResult && (
              <span
                className={`flex items-center gap-1 text-sm ${
                  testResult.ok ? 'text-green-600' : 'text-destructive'
                }`}
              >
                {testResult.ok ? (
                  <>
                    <Check className="h-4 w-4" />
                    200 · {testResult.latencyMs} ms
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4" />
                    {testResult.httpStatus} {testResult.error ?? 'failed'}
                  </>
                )}
              </span>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleSaveWithoutTesting}
            disabled={saving || testing || !label.trim()}
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50 self-start sm:self-auto"
          >
            Save without testing
          </button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={() => handleSave(false)} disabled={saveDisabled}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
